import { Volume2, VolumeX } from "lucide-react"
import { FrameFormatDropdown } from "@/components/ui/frame-format-select"
import { SearchableCombobox } from "@/components/ui/searchable-combobox"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import { SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { VideoDurationPopover } from "@/components/ui/video-duration-popover"
import { useTranslation } from "@/hooks/use-translation"
import { writeGenerateAudioPref } from "@/lib/canvas-model-defaults"
import type { FrameFormat } from "@/lib/frame-format"
import { seedanceDraftFamily } from "@/lib/seedance-draft"
import { withAdaptiveDurationOption } from "@/lib/video-duration"
import { cn } from "@/lib/utils"
import type { CanvasMediaKind, CanvasModel, CanvasModelExtraParam } from "@/types/canvas"

const SELECT_CLASS =
  "h-8 rounded-lg border border-ui-border bg-ui-surface-sunken py-0 pr-8 pl-2 text-xs leading-none text-ui-text-primary focus-visible:outline-none"

export type GenerationAttributeBarProps = {
  mediaKind: CanvasMediaKind
  modelId: string
  modelOptions: { value: string; label: string; disabled?: boolean }[]
  onSelectModel: (modelId: string) => void
  selectedModel: CanvasModel | null
  disabled?: boolean
  aspectRatio: string
  formatOptions: FrameFormat[]
  onAspectRatio: (format: FrameFormat) => void
  durationSec: number
  durationOptions: readonly number[]
  adaptiveAllowed?: boolean
  onDuration: (durationSec: number) => void
  modelParams: Record<string, string>
  onModelParams: (modelParams: Record<string, string>) => void
  generateAudioAutoHint?: boolean
  /** Проектный дефолт, когда в model_params нет ключа draft. */
  seedanceDraftDefault?: boolean
  /** Wan multi-shot lives inside the duration popover. Video nodes omit it. */
  multiShot?: {
    checked: boolean
    onChange: (multi: boolean) => void
  }
}

function qualityParam(
  model: CanvasModel | null,
  mediaKind: CanvasMediaKind,
): CanvasModel["extra_params"][number] | null {
  if (!model) return null
  const key = mediaKind === "video" ? "resolution" : "image_size"
  return model.extra_params.find((param) => param.key === key && param.kind === "select") ?? null
}

function audioParam(model: CanvasModel | null, mediaKind: CanvasMediaKind): CanvasModelExtraParam | null {
  if (!model || mediaKind !== "video") return null
  return model.extra_params.find((param) => param.key === "generate_audio" && param.kind === "boolean") ?? null
}

export function GenerationAttributeBar({
  mediaKind,
  modelId,
  modelOptions,
  onSelectModel,
  selectedModel,
  disabled = false,
  aspectRatio,
  formatOptions,
  onAspectRatio,
  durationSec,
  durationOptions,
  adaptiveAllowed = false,
  onDuration,
  modelParams,
  onModelParams,
  generateAudioAutoHint = false,
  seedanceDraftDefault = false,
  multiShot,
}: GenerationAttributeBarProps) {
  const { t } = useTranslation("workspace")
  const quality = qualityParam(selectedModel, mediaKind)
  const audio = audioParam(selectedModel, mediaKind)
  const qualityValue = quality ? (modelParams[quality.key] ?? quality.default) : ""
  const draftEligible =
    mediaKind === "video" && seedanceDraftFamily(selectedModel?.id) != null && qualityValue === "480p"
  const draftRaw = modelParams.draft
  const draftOn = draftRaw == null || draftRaw === "" ? seedanceDraftDefault : draftRaw === "true"
  const audioEnabled = audio ? (modelParams[audio.key] ?? audio.default) === "true" : false
  const durationChoices = withAdaptiveDurationOption(durationOptions, adaptiveAllowed)

  return (
    <>
      <SearchableCombobox
        value={modelId}
        onChange={onSelectModel}
        options={modelOptions}
        placeholder={t("canvas.panel.model")}
        disabled={disabled}
        dropdownDirection="up"
        dropdownAutoWidth
        className="w-[min(100%,10rem)] min-w-[6rem]"
        inputClassName={cn(SELECT_CLASS, "w-full pr-2")}
      />

      {formatOptions.length > 0 ? (
        <FrameFormatDropdown
          value={aspectRatio}
          options={formatOptions}
          onChange={(format) => {
            if (format === "fill") return
            onAspectRatio(format)
          }}
          dropdownDirection="up"
          disabled={disabled}
          ariaLabel={t("canvas.panel.aspectRatio")}
        />
      ) : null}

      {mediaKind === "video" && selectedModel && durationChoices.length > 0 ? (
        <VideoDurationPopover
          value={durationSec}
          options={durationChoices}
          placement="above"
          onChange={onDuration}
          multiShot={multiShot}
          className={disabled ? "pointer-events-none opacity-40" : undefined}
        />
      ) : null}

      {quality ? (
        <SelectDropdown
          value={qualityValue}
          onChange={(next) => {
            const params = { ...modelParams, [quality.key]: next }
            if (quality.key === "resolution" && next !== "480p") delete params.draft
            onModelParams(params)
          }}
          options={quality.options.map((opt) => ({ value: opt, label: opt }))}
          dropdownDirection="up"
          ariaLabel={t("canvas.panel.quality")}
          disabled={disabled}
          fitContent
          buttonClassName={cn(SELECT_CLASS, "pr-2")}
        />
      ) : null}

      {draftEligible ? (
        <button
          type="button"
          role="switch"
          aria-checked={draftOn}
          disabled={disabled}
          onClick={() => onModelParams({ ...modelParams, draft: draftOn ? "false" : "true" })}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-lg border border-ui-border px-2 text-xs disabled:opacity-40",
            draftOn ? "text-ui-text-primary" : "text-ui-text-secondary hover:text-ui-text-primary",
          )}
        >
          <span
            className={cn(
              "h-3.5 w-3.5 rounded-sm border",
              draftOn ? "border-ui-accent bg-ui-accent" : "border-ui-border-strong",
            )}
          />
          {t("canvas.panel.draft")}
        </button>
      ) : null}

      {audio ? (
        <SidebarTooltip
          label={t(generateAudioAutoHint ? "canvas.panel.generateAudioEnabledHint" : "canvas.panel.generateAudio")}
          side="top"
          open={generateAudioAutoHint ? true : undefined}
        >
          <button
            type="button"
            role="switch"
            aria-checked={audioEnabled}
            aria-label={t("canvas.panel.generateAudio")}
            disabled={disabled}
            onClick={() => {
              const next = !audioEnabled
              writeGenerateAudioPref(next)
              onModelParams({
                ...modelParams,
                [audio.key]: next ? "true" : "false",
              })
            }}
            className={cn(
              "inline-flex items-center rounded-lg px-2 py-1 transition-colors disabled:opacity-40",
              audioEnabled ? "text-ui-text-primary" : "text-ui-text-secondary hover:text-ui-text-primary",
            )}
          >
            {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </SidebarTooltip>
      ) : null}
    </>
  )
}
